import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { AISummary } from "../components/AISummary";
import { SpendingPredictions } from "../components/SpendingPredictions";
import { 
  Lightbulb, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const INSIGHT_ICONS = {
  positive: CheckCircle2,
  warning: AlertTriangle,
  tip: Lightbulb,
};

const INSIGHT_COLORS = {
  positive: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30",
  warning: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30",
  tip: "text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30",
};

export const InsightsPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState([]);
  const [cashFlow, setCashFlow] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [insightsRes, cashFlowRes] = await Promise.all([
        api.get("/insights/spending"),
        api.get("/cash-flow")
      ]);
      setInsights(insightsRes.data);
      setCashFlow(cashFlowRes.data);
    } catch (error) {
      console.error("Failed to fetch insights:", error);
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="insights-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 rounded-2xl" />
        <div className="space-y-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const netPositive = (cashFlow?.net_cash_flow || 0) >= 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Insights</h1>
          <p className="text-muted-foreground mt-1">
            Analyze your spending data and improve your budget
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="refresh-insights">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* AI Summary */}
      <AISummary page="insights" />

      {/* AI Spending Predictions */}
      <SpendingPredictions />

      {/* Cash Flow Summary */}
      <Card className="border-border/50 bg-gradient-to-br from-primary/5 to-transparent" data-testid="cash-flow-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {netPositive ? (
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-500" />
            )}
            Cash Flow - {cashFlow?.period}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">Recurring Income</p>
              <p className="font-heading text-xl font-bold text-emerald-600">
                {formatCurrency(cashFlow?.recurring_income || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Recurring Expenses</p>
              <p className="font-heading text-xl font-bold">
                {formatCurrency(cashFlow?.recurring_expenses || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Variable Expenses</p>
              <p className="font-heading text-xl font-bold">
                {formatCurrency(cashFlow?.variable_expenses || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Cash Flow</p>
              <p className={`font-heading text-xl font-bold ${
                netPositive ? "text-emerald-600" : "text-red-600"
              }`}>
                {netPositive ? "+" : ""}{formatCurrency(cashFlow?.net_cash_flow || 0)}
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border/50">
            <p className="text-sm text-muted-foreground">
              Projected end-of-month balance: 
              <span className="font-semibold text-foreground ml-2">
                {formatCurrency(cashFlow?.projected_balance || 0)}
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Spending Insights */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold">Spending Insights</h2>
        
        {insights.length === 0 ? (
          <Card className="border-border/50">
            <CardContent className="py-12 text-center">
              <Lightbulb className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
              <h3 className="font-heading font-semibold mb-2">No insights yet</h3>
              <p className="text-muted-foreground">
                Keep using the app and we&apos;ll provide personalized insights based on your spending.
              </p>
            </CardContent>
          </Card>
        ) : (
          insights.map((insight, i) => {
            const Icon = INSIGHT_ICONS[insight.type] || Lightbulb;
            const colorClass = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.tip;
            
            return (
              <Card key={i} className="border-border/50" data-testid={`insight-${i}`}>
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold">{insight.title}</h3>
                          <p className="text-muted-foreground mt-1">{insight.description}</p>
                        </div>
                        {insight.percentage_change !== null && (
                          <Badge 
                            variant="secondary"
                            className={insight.percentage_change > 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}
                          >
                            {insight.percentage_change > 0 ? "+" : ""}{insight.percentage_change}%
                          </Badge>
                        )}
                      </div>
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm">
                          <span className="font-medium">💡 Recommendation:</span> {insight.recommendation}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
